import { useCallback, useState } from 'react';

import type { AdminUserListFilters, AdminUserSelection } from '@/client/types/admin/users-orgs';

/**
 * Row selection that survives paging, plus a Gmail-style "all N matching" mode.
 * Clears whenever `filtersKey` changes — a selection made under one set of
 * filters must never be acted on under another.
 */
export function useUserSelection(filtersKey: string) {
  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [key, setKey] = useState(filtersKey);

  if (key !== filtersKey) {
    setKey(filtersKey);
    setSelected(new Set());
    setAllMatching(false);
  }

  const toggle = useCallback(
    (uid: number, pageUids: number[]) => {
      if (allMatching) {
        // Leaving "all matching" keeps this page, minus the row just unticked.
        setAllMatching(false);
        setSelected(new Set(pageUids.filter((u) => u !== uid)));
        return;
      }
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(uid)) next.delete(uid);
        else next.add(uid);
        return next;
      });
    },
    [allMatching],
  );

  const setPage = useCallback((pageUids: number[], checked: boolean) => {
    setAllMatching(false);
    setSelected((prev) => {
      const next = new Set(prev);
      pageUids.forEach((u) => (checked ? next.add(u) : next.delete(u)));
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setSelected(new Set());
    setAllMatching(false);
  }, []);

  const isSelected = (uid: number) => allMatching || selected.has(uid);

  /** What to send to bulk/export; `total` is the match count the admin saw. */
  const toSelection = (filters: AdminUserListFilters, total: number): AdminUserSelection =>
    allMatching ? { filters, expected_total: total } : { uids: [...selected] };

  return {
    selected,
    allMatching,
    selectAllMatching: () => setAllMatching(true),
    toggle,
    setPage,
    clear,
    isSelected,
    count: (total: number) => (allMatching ? total : selected.size),
    toSelection,
  };
}
